import { nanoid } from 'nanoid';

import type {
  CreateOilLogFormValues,
  OilLog,
  OilLogEntry,
  UpdateOilLogFormValues,
} from '@/store/oil-log/oil-log.dto';
import { convertPerVolume, convertVolume, round2, type VolumeUnit } from '@/utils/unitConversion';

import { TIME_RE, type OilEntry, type OilLogState } from './types';

// Oil logs are split per day, so an entry only captures the time of day. We
// merge the log's selected date with the entry time into a single ISO
// `date_time` for the backend, and split it back apart when loading.
const mergeDateTime = (dateIso: string, time: string): string => {
  const base = new Date(dateIso);
  if (Number.isNaN(base.getTime())) return time;
  const match = TIME_RE.exec(time.trim());
  if (!match) return base.toISOString();
  const merged = new Date(
    Date.UTC(
      base.getUTCFullYear(),
      base.getUTCMonth(),
      base.getUTCDate(),
      Number(match[1]),
      Number(match[2]),
    ),
  );
  return merged.toISOString();
};

const extractTime = (dateTimeIso: string): string => {
  const d = new Date(dateTimeIso);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

// A figure that is not a number is kept as written.
const convertFigure = (value: string, convert: (n: number) => number): string => {
  const trimmed = value.trim();
  if (trimmed === '') return value;
  const n = Number(trimmed);
  return Number.isFinite(n) ? String(round2(convert(n))) : value;
};

/** The volume figures of an entry, each named with the field that holds its unit. */
export const UNIT_OF = {
  bunkerTakenUsg: 'bunkerTakenUnit',
  costPerUsgAfterTaxUsd: 'costPerUnit',
  totalOnboardUsg: 'totalOnboardUnit',
} as const;

export type VolumeFigure = keyof typeof UNIT_OF;

/** A figure as it was keyed in, and what a unit switch last showed in its place. */
export type FigureOrigin = { unit: VolumeUnit; value: string; shown: string };

const convertTo: Record<VolumeFigure, (n: number, from: VolumeUnit, to: VolumeUnit) => number> = {
  bunkerTakenUsg: convertVolume,
  // A price per unit converts inversely.
  costPerUsgAfterTaxUsd: convertPerVolume,
  totalOnboardUsg: convertVolume,
};

/**
 * Re-expresses one volume figure in another unit, for the unit switch beside its field. It
 * converts from the value it was keyed in as, not from the rounded figure an earlier switch
 * showed, so switching back and forth never drifts: $3.50/gal → L → gal is $3.50 again. A figure
 * edited since the last switch counts as keyed in anew.
 */
export const switchFigureUnit = (
  figure: VolumeFigure,
  value: string,
  origin: FigureOrigin | undefined,
  from: VolumeUnit,
  to: VolumeUnit,
): FigureOrigin => {
  const source = origin && origin.shown === value ? origin : { unit: from, value };
  const shown =
    source.unit === to
      ? source.value
      : convertFigure(source.value, (n) => convertTo[figure](n, source.unit, to));
  return { unit: source.unit, value: source.value, shown };
};

type CostFigures = Pick<
  OilEntry,
  'bunkerTakenUsg' | 'bunkerTakenUnit' | 'costPerUsgAfterTaxUsd' | 'costPerUnit'
>;

/**
 * What an entry's bunker cost in USD: the volume taken times the price per unit, the volume first
 * put in the unit the price is per — gallons off the gauges may be priced per liter. Empty until
 * both are numbers.
 */
export const entryCostUsd = (entry: CostFigures): string => {
  const { bunkerTakenUsg, costPerUsgAfterTaxUsd } = entry;
  if (bunkerTakenUsg.trim() === '' || costPerUsgAfterTaxUsd.trim() === '') return '';
  const volume = convertVolume(Number(bunkerTakenUsg), entry.bunkerTakenUnit, entry.costPerUnit);
  const cost = volume * Number(costPerUsgAfterTaxUsd);
  return Number.isFinite(cost) ? String(round2(cost)) : '';
};

/** A cost that is not the product of its entry's figures was typed in by hand. */
export const isCostTyped = (entry: CostFigures & Pick<OilEntry, 'costUsd'>): boolean =>
  entry.costUsd.trim() !== '' && Number(entry.costUsd) !== Number(entryCostUsd(entry));

// Entries go out as keyed, each figure with its own unit; the service stores them as they are.
const entryToDto = (entry: OilEntry, date: string): OilLogEntry => ({
  dateTime: mergeDateTime(date, entry.time),
  location: entry.location,
  bunkerTakenUsg: entry.bunkerTakenUsg,
  costPerUsgAfterTaxUsd: entry.costPerUsgAfterTaxUsd,
  costUsd: entry.costUsd,
  costLocalCurrency: entry.costLocalCurrency,
  totalOnboardUsg: entry.totalOnboardUsg,
  bunkerTakenUnit: entry.bunkerTakenUnit,
  costPerUnit: entry.costPerUnit,
  totalOnboardUnit: entry.totalOnboardUnit,
});

// Each figure is shown in the unit it was saved in (gallons when saved without one), whatever the
// viewer's preference.
const entryFromDto = (entry: OilLogEntry): OilEntry => ({
  id: nanoid(),
  time: extractTime(entry.dateTime),
  location: entry.location,
  bunkerTakenUsg: entry.bunkerTakenUsg,
  costPerUsgAfterTaxUsd: entry.costPerUsgAfterTaxUsd,
  costUsd: entry.costUsd,
  costLocalCurrency: entry.costLocalCurrency,
  totalOnboardUsg: entry.totalOnboardUsg,
  bunkerTakenUnit: entry.bunkerTakenUnit,
  costPerUnit: entry.costPerUnit,
  totalOnboardUnit: entry.totalOnboardUnit,
});

export const oilLogToFormState = (log: OilLog): OilLogState => ({
  date: log.date,
  entries: log.entries.map(entryFromDto),
});

export const formStateToCreateValues = (state: OilLogState): CreateOilLogFormValues => ({
  date: state.date,
  entries: state.entries.map((entry) => entryToDto(entry, state.date)),
});

export const formStateToUpdateValues = (
  id: string,
  state: OilLogState,
): UpdateOilLogFormValues => ({
  id,
  entries: state.entries.map((entry) => entryToDto(entry, state.date)),
});
